/**
 * Web Component entry point for the Profile Edit widget.
 *
 * Importing this module registers the <legion-profile-edit> custom element
 * so it can be used in any HTML page or framework that supports web components.
 *
 * @example
 * ```ts
 * import '@legionhandtech/lht-react-widgets/profile-edit-web-component';
 *
 * // Then use in HTML:
 * // <legion-profile-edit api-url="https://your-domain.com" auth-token="jwt" />
 * ```
 */

import { PROFILE_EDIT_THEMING_PROP_TO_PARAM } from "./types";
import { attachAuthBridge, type AuthBridgeHandle, AUTH_BRIDGE_OBSERVED_ATTRS } from "../shared/auth-bridge";

const PROFILE_EDIT_CSS_ATTRIBUTES = Object.values(
  PROFILE_EDIT_THEMING_PROP_TO_PARAM
);

class LegionProfileEditElement extends HTMLElement {
  public iframe: HTMLIFrameElement | null = null;
  private authBridge: AuthBridgeHandle | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  static get observedAttributes(): string[] {
    return [
      "api-url",
      
      "width",
      "height",
      "theme",
      ...PROFILE_EDIT_CSS_ATTRIBUTES,
    
      ...AUTH_BRIDGE_OBSERVED_ATTRS,
    ];
  }

  connectedCallback(): void {
    this.authBridge = attachAuthBridge(this, {
      iframeAccessor: () => this.iframe,
      baseUrl: this.bridgeBaseUrl,
    });
    this.render();
  }

  disconnectedCallback(): void {
    this.authBridge?.detach();
    this.authBridge = null;
  }

  attributeChangedCallback(
    _name: string,
    oldValue: string | null,
    newValue: string | null
  ): void {
    if (oldValue !== newValue) {
      this.render();
    }
  }

  private get apiUrl(): string {
    return this.getAttribute("api-url") || window.location.origin;
  }

  private get widgetWidth(): string {
    return this.getAttribute("width") || "600";
  }

  private get widgetHeight(): string {
    return this.getAttribute("height") || "800";
  }

  private get theme(): string {
    return this.getAttribute("theme") || "light";
  }

  render(): void {
    if (!this.shadowRoot) return;

    const iframe = document.createElement("iframe");
    const url = new URL("/widget/profile-edit", this.apiUrl);

    url.searchParams.set("theme", this.theme);

    PROFILE_EDIT_CSS_ATTRIBUTES.forEach((attr) => {
      const val = this.getAttribute(attr);
      if (val !== null && val !== undefined && String(val).length > 0) {
        url.searchParams.set(attr, val);
      }
    });

    iframe.src = url.toString();
    iframe.width = this.widgetWidth;
    iframe.height = this.widgetHeight;
    iframe.frameBorder = "0";
    iframe.scrolling = "no";
    iframe.title = "Profile Edit Widget";

    iframe.onload = () => {
      this.authBridge?.postCurrentToken();
      this.dispatchEvent(
        new CustomEvent("load", {
          detail: { widget: "profile-edit", theme: this.theme },
        })
      );
    };

    iframe.onerror = () => {
      if (!this.shadowRoot) return;
      this.shadowRoot.innerHTML = `<div style="padding:32px;text-align:center;color:#856404;background:#fff3cd;border:1px solid #ffeaa7;border-radius:8px;">Failed to load profile edit widget</div>`;
    };

    const style = document.createElement("style");
    style.textContent = `
      :host {
        display: block;
        width: ${this.widgetWidth}px;
        height: ${this.widgetHeight}px;
      }
      iframe {
        width: 100%;
        height: 100%;
        border: none;
        border-radius: 8px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        background: ${this.theme === "dark" ? "#1a1a1a" : "white"};
      }
    `;

    this.shadowRoot.innerHTML = "";
    this.shadowRoot.appendChild(style);
    this.shadowRoot.appendChild(iframe);
    this.iframe = iframe;
  }

  private get bridgeBaseUrl(): string {
    return this.apiUrl;
  }
}

if (
  typeof customElements !== "undefined" &&
  !customElements.get("legion-profile-edit")
) {
  customElements.define("legion-profile-edit", LegionProfileEditElement);
}

export { LegionProfileEditElement };

